import { useId, useState } from 'react';
import type { JSX } from 'react';
import { Icon } from '@ndrc/ui';
import { t } from '../messages';

/**
 * Un champ de mot de passe **avec l'œil qui le montre**. Le seul champ de mot de passe du bureau :
 * l'écran de connexion et « Mon compte » passent tous les deux par ici, pour que le geste soit le
 * même aux deux endroits.
 *
 * Ce n'est pas un ornement. Les élèves recopient un mot de passe imprimé — `montagne-tapis-66` —
 * depuis une fiche papier, en classe, sous le regard des autres, et n'ont aucun moyen de savoir
 * lequel des trois `montagne-tapis-6` qu'ils ont tapés est le bon. Sans l'œil, une faute de frappe
 * ne se distingue pas d'un mauvais mot de passe.
 *
 * Trois choses le rendent utilisable pour de vrai :
 *
 * - le bouton est un vrai `<button type="button">` : il se prend au clavier (Tab depuis le champ,
 *   Entrée ou Espace) et n'envoie jamais le formulaire ;
 * - son intitulé dit ce qu'il fera, en français, et change avec l'état — « Afficher le mot de
 *   passe » / « Masquer le mot de passe » — doublé d'`aria-pressed` pour les lecteurs d'écran ;
 * - le champ est révélé, jamais recopié ailleurs : `type` passe de `password` à `text`, la valeur
 *   ne bouge pas, le gestionnaire de mots de passe du navigateur continue de faire son travail
 *   grâce à `autoComplete`.
 *
 * `label` et `htmlFor` sont explicites (`useId`) plutôt qu'un `<input>` enveloppé dans le
 * `<label>` : un bouton à l'intérieur d'un `<label>` renvoie son clic au champ, ce qui ferait
 * clignoter le curseur à chaque appui sur l'œil.
 */
export interface PasswordFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** `current-password` à la connexion, `new-password` pour un changement — jamais deviné. */
  autoComplete: 'current-password' | 'new-password';
  name?: string;
  autoFocus?: boolean;
}

export function PasswordField({
  label,
  value,
  onChange,
  autoComplete,
  name,
  autoFocus = false,
}: PasswordFieldProps): JSX.Element {
  const id = useId();
  const [revealed, setRevealed] = useState(false);
  const action = revealed ? t('field.hidePassword') : t('field.revealPassword');

  return (
    <div className="ndrc-field">
      <label className="ndrc-label" htmlFor={id}>
        {label}
      </label>
      <div className="ndrc-password">
        <input
          id={id}
          className="ndrc-input ndrc-input--password"
          type={revealed ? 'text' : 'password'}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          {...(name === undefined ? {} : { name })}
        />
        <button
          type="button"
          className="ndrc-reveal"
          aria-pressed={revealed}
          aria-label={action}
          title={action}
          onClick={() => {
            setRevealed((shown) => !shown);
          }}
        >
          <Icon name={revealed ? 'eye-off' : 'eye'} size={16} strokeWidth={1.6} />
        </button>
      </div>
    </div>
  );
}
